import { useEffect, useState } from "react";
import TablaTareasVista from "../vista/tablaTareasVista";
import type { ITarea } from "../../../modelos/Tarea/entidades/ITarea";
import { getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, type SortingState } from "@tanstack/react-table";
import { columnasExcel, columnasTablaTarea } from "../vista/constantesTablaTareas";
import { estadoInicialFiltroTarea, estadoInicialTarea, crearEstadoInicialTarea, type IEntidadesRegistroTarea } from "../interfaces";
import { TareaModelo } from "../../../modelos/Tarea";
import { ClienteModelo } from "../../../modelos/Clientes";
import { ProyectoModelo } from "../../../modelos/Proyectos";
import { TipoTareaModelo } from "../../../modelos/TipoTarea";
import { ModuloModelo } from "../../../modelos/ModulosCheckList";
import ModeloUsuario from "../../../modelos/Usuario";
import { useNotificaciones } from "../../../globales/contextos/notificationContext";
import { EnumTipoNotificacion } from "../../../globales/interfaces";
import { useCargando } from "../../../globales/contextos/cargandoContext";
import ModalTareaCV from "./modalTareaCV";
import ModalCancelarTareaCV from "./modalCancelarTareaCV";
import { EnumPerfiles } from "../../../globales/enums/enumPerfiles";
import type { IFiltroTarea } from "../../../modelos/Tarea/entidades/IFiltroTarea";
import { estadoInicialFiltroProyecto } from "../../proyectos/interfaces";
import { estadoInicialFiltroUsuario } from "../../usuarios/interfaces";
import { EstadoTareaModelo } from "../../../modelos/EstadoTarea";
import { convertirTareaAFormulario } from "../utils/tareaUtils";
import ModeloCausalEmergencia from "../../../modelos/CausalEmergencia";
import type { ITareaFormulario } from "../interfaces/ITareaFormulario";
import { estadoInicialFiltroCliente } from "../../clientes/interfaces";
import { ModuloTareaModelo } from "../../../modelos/ModulosTarea";
import ModalExportacionExcelVista from "../vista/modalExportacionExcelVista";
import { exportarExcel } from "../../../globales/funciones/exportarExcel";
import { UbicacionModelo } from "../../../modelos/Ubicacion";
import type { IRespuestaCargaMasiva } from "../../../modelos/Tarea/entidades/IRespuestaCargaMasiva";
import ModalErroresCargaMasiva from "../vista/modalErroresCargaMasiva";
import { useAutenticacion } from "../../../globales/contextos/authContext";
import { EnumPermisos } from "../../../globales/enums/enumPermisos";
import { EnumTipoTarea } from "../enums/enumTipoTarea";
import { codigosVisitaInstalacionConsulta } from "../utils/permisosTareaUtils";

const TareasCV = () => {
    const modeloTarea = new TareaModelo();
    const modeloCliente = new ClienteModelo();
    const modeloProyecto = new ProyectoModelo();
    const modeloTipoTarea = new TipoTareaModelo();
    const modeloUsuario = new ModeloUsuario();
    const modeloEstadoTarea = new EstadoTareaModelo();
    const modeloModulosTarea = new ModuloTareaModelo();
    const modeloModulo = new ModuloModelo();
    const modeloCausalEmergencia = new ModeloCausalEmergencia();
    const modeloUbicacion = new UbicacionModelo();

    const { mostrarNotificacion } = useNotificaciones();
    const { manejarCargando } = useCargando();
    const { usuario } = useAutenticacion();

    // Con este permiso (ej. Planeador) solo se ven tareas de Visita Instalación: el filtro por tipo queda fijo
    const soloVisitaInstalacion = !!usuario?.Permisos?.some(p => p.Codigo === EnumPermisos.PERMISO_TAREAS_VER_SOLO_INSTALACION);
    const filtroInicialTarea: IFiltroTarea = soloVisitaInstalacion
        ? { ...estadoInicialFiltroTarea, TipoServicioId: EnumTipoTarea.VisitaInstalacion }
        : estadoInicialFiltroTarea;
    // Sin permiso para todas las actividades NBS (Analista), de Visita Instalación solo se consultan las de Verificación.
    const codigosVisitaInstalacion = codigosVisitaInstalacionConsulta(usuario?.Permisos?.map(p => p.Codigo) ?? []);
    // Se aplica antes de cada consulta (lista y exportación) para que el tipo y las actividades nunca se envíen distintos
    const aplicarRestriccionFiltro = (filtro: IFiltroTarea): IFiltroTarea => ({
        ...filtro,
        ...(soloVisitaInstalacion ? { TipoServicioId: EnumTipoTarea.VisitaInstalacion } : {}),
        CodigosVisitaInstalacion: codigosVisitaInstalacion
    });

    const PAGE_SIZE = 15;
    const [pageIndex, setPageIndex] = useState(0);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [totalTareas, setTotalTareas] = useState(0);

    const [listaTareas, setListaTareas] = useState<ITarea[]>([]);
    const [habilitarFiltros, setHabilitarFiltros] = useState<boolean>(true);
    const [filtroTarea, setFiltroTarea] = useState<IFiltroTarea>(filtroInicialTarea);
    const [abrirModal, setAbrirModal] = useState(false);
    const [abrirModalCancelar, setAbrirModalCancelar] = useState(false);
    const [tareaParaCancelar, setTareaParaCancelar] = useState<ITarea>(estadoInicialTarea);

    const [entidadTarea, setEntidadTarea] = useState<ITareaFormulario>(estadoInicialTarea)
    const [entidadesRegistro, setEntidadesRegistro] = useState<IEntidadesRegistroTarea>({
        Proyectos: [],
        Clientes: [],
        Tecnicos: [],
        TecnicosTodos: [],
        Equipos: [],
        TiposTarea: [],
        ModulosCheckList: [],
        ModulosCheckListTodos: [],
        CausalesEmergencia: [],
        ClientesTodos: [],
        EquiposTodos: [],
        EstadosTarea: [],
        ProyectosTodos: [],
        Paises: [],
        HabilitacionesNBS: []
    });

    const [visualizacion, setVisualizacion] = useState<boolean>(false);

    const [estadoModalExportacionExcel, setEstadoModalExportacionExcel] = useState<boolean>(false);
    const PAGE_SIZE_EXPORT = 15;
    const [pageIndexExportacion, setPageIndexExportacion] = useState(0);
    const [sortingExportacion, setSortingExportacion] = useState<SortingState>([]);

    const [listaTareasConsultaExportacion, setListaTareasConsultaExportacion] = useState<ITarea[]>([]);
    const [habilitarFiltrosExportacion, setHabilitarFiltrosExportacion] = useState<boolean>(true);
    const [filtroTareaExportacion, setFiltroTareaExportacion] = useState<IFiltroTarea>(filtroTarea);

    const [abrirModalErrores, setAbrirModalErrores] = useState(false);
    const [datosRespuestaCarga, setDatosRespuestaCarga] = useState<IRespuestaCargaMasiva>({
        TareasGuardadas: 0,
        TareasRechazadas: 0,
        Rechazadas: []
    });

    useEffect(() => {
        consultarTareas(filtroInicialTarea);
        consultarEntidadesRegistro();
    }, []);

    const consultarEntidadesRegistro = async () => {
        try {
            manejarCargando(true, undefined, true);
            const [clientes, proyectos, tiposTarea, tecnicos, estadosTarea, paises, modulos, causalesEmergencia] = await Promise.all([
                modeloCliente.obtenerListaClientes(estadoInicialFiltroCliente),
                modeloProyecto.obtenerListaProyectos(estadoInicialFiltroProyecto.NitProyecto),
                modeloTipoTarea.obtenerListaTiposTarea(),
                modeloUsuario.obtenerListaUsuarios(estadoInicialFiltroUsuario),
                modeloEstadoTarea.obtenerListaEstadosTarea(),
                modeloUbicacion.obtenerPaises(),
                modeloModulo.obtenerListaModulos(),
                modeloCausalEmergencia.obtenerListaCausalesEmergencia()
            ]);

            setEntidadesRegistro({
                Proyectos: proyectos.Data.filter(p => p.Activo && p.Eliminado === false),
                ClientesTodos: clientes.Data,
                EquiposTodos: [],
                ProyectosTodos: proyectos.Data.filter(p => p.Activo && p.Eliminado === false),
                Clientes: clientes.Data,
                Tecnicos: tecnicos.Data.filter(u => u.PerfilUsuarioId === EnumPerfiles.Tecnico),
                TecnicosTodos: tecnicos.Data.filter(u => u.PerfilUsuarioId === EnumPerfiles.Tecnico),
                Equipos: [],
                TiposTarea: soloVisitaInstalacion
                    ? tiposTarea.Data.filter(t => t.Id === EnumTipoTarea.VisitaInstalacion)
                    : tiposTarea.Data,
                ModulosCheckList: modulos.Data.Modulos,
                ModulosCheckListTodos: modulos.Data.Modulos,
                EstadosTarea: estadosTarea.Data,
                CausalesEmergencia: causalesEmergencia.Data,
                Paises: paises.Data,
                HabilitacionesNBS: []
            });
        } catch {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                'Error al cargar datos de filtros',
                'No fue posible cargar los datos necesarios para los filtros. Por favor verifica tu conexión e intenta nuevamente.',
                5000
            );
        } finally {
            manejarCargando(false);
        }
    }

    const consultarTareas = async (filtroAUsar: IFiltroTarea = filtroTarea, paginaAMostrar: number = pageIndex) => {
        try {
            manejarCargando(true, undefined, true);
            // Se envía de forma independiente el paginador
            const tareas = await modeloTarea.obtenerListaTareas(aplicarRestriccionFiltro(filtroAUsar), { Page: paginaAMostrar + 1, Size: PAGE_SIZE });
            setTotalTareas(tareas.Pagination?.Total ?? tareas.Data.length);
            setListaTareas(tareas.Data);
        }
        catch {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                'Error al consultar tareas',
                'No fue posible consultar las tareas. Por favor verifica tu conexión e intenta nuevamente.',
                5000
            );
        }
        finally {
            manejarCargando(false);
        }
    }

    const manejarHabilitarFiltros = (habilitar: boolean) => {
        setHabilitarFiltros(habilitar)
    }

    const manejarCambioFiltroTarea = async (campo: keyof IFiltroTarea, valor: IFiltroTarea[keyof IFiltroTarea]) => {
        if (soloVisitaInstalacion && campo === "TipoServicioId") return;
        setFiltroTarea((prevFiltroTarea) => ({
            ...prevFiltroTarea,
            [campo]: valor
        }));
        setFiltroTareaExportacion((prevFiltroTarea) => ({
            ...prevFiltroTarea,
            [campo]: valor
        }));
    }

    // const cancelarTarea = (tarea: ITarea) => {
    //     setTareaParaCancelar(tarea);
    //     abrirModalCancelarTarea(true);
    // }


    const tabla = useReactTable({
        data: listaTareas,
        columns: [],
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        manualPagination: true,
        pageCount: Math.ceil(totalTareas / PAGE_SIZE),
        state: { pagination: { pageIndex, pageSize: PAGE_SIZE }, sorting },
        onPaginationChange: updater => {
            if (typeof updater === 'function') {
                const newState = updater({ pageIndex, pageSize: PAGE_SIZE });
                setPageIndex(newState.pageIndex);
                consultarTareas(filtroTarea, newState.pageIndex);
            } else {
                setPageIndex(updater.pageIndex ?? 0);
                consultarTareas(filtroTarea, updater.pageIndex ?? 0);
            }
        },
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
    });

    const columns = columnasTablaTarea({
        manejarEdicionTarea: (tarea: ITarea, visualizacion?: boolean) => abrirModalTarea(true, tarea, visualizacion),
        manejarCancelacionTarea: (tarea) => abrirModalCancelarTarea(true, tarea),
        estadoModalExportacionExcel: false
    });

    tabla.setOptions((prev) => ({ ...prev, columns }));

    const abrirModalTarea = async (estadoModal: boolean, tarea: ITarea | null, visualizacion?: boolean) => {
        if (tarea) {
            const tareaMapeada = convertirTareaAFormulario({ ...tarea, EstadoId: tarea.EstadoTareaId || 0, EstadoTareaId: undefined } as ITarea);
            setEntidadTarea({
                ...tareaMapeada,
            });
            if (tarea.Id) {
                try {

                    manejarCargando(true, undefined, true);
                    const modulosTarea = await modeloModulosTarea.obtenerListaModulosTarea(tarea.Id!);
                    setEntidadTarea({
                        ...tareaMapeada,
                        ModulosChecklist: modulosTarea.Data.map(m => m.IdModuloCatalogo)
                    });


                }
                catch (e) {
                    mostrarNotificacion(
                        EnumTipoNotificacion.Error,
                        'Error al cargar módulos de tarea',
                        'No fue posible cargar los módulos asociados a la tarea. Por favor verifica tu conexión e intenta nuevamente.',
                        5000
                    );
                }
                finally {
                    manejarCargando(false);
                }

            } else {
                setEntidadTarea(crearEstadoInicialTarea());
            }

        }

        if (visualizacion) {
            setVisualizacion(visualizacion);
        } else setVisualizacion(false);

        if (!estadoModal) {
            setEntidadTarea(crearEstadoInicialTarea());
        }
        setAbrirModal(estadoModal);
    };

    const abrirModalCancelarTarea = (estadoModal: boolean, tarea: ITarea | null) => {
        setAbrirModalCancelar(estadoModal);
        if (!estadoModal) {
            limpiarFiltros();
        }
        if (tarea) {
            setTareaParaCancelar(tarea);
        }
    };

    const limpiarFiltros = async () => {
        setFiltroTarea(filtroInicialTarea);
        setFiltroTareaExportacion(filtroInicialTarea);
        setPageIndex(0);
        try {
            manejarCargando(true);
            const tareas = await modeloTarea.obtenerListaTareas(aplicarRestriccionFiltro(filtroInicialTarea), { Page: 1, Size: PAGE_SIZE });
            setTotalTareas(tareas.Pagination?.Total ?? tareas.Data.length);
            setListaTareas(tareas.Data);
        } catch (e) {
            console.error(e);
        } finally {
            manejarCargando(false);
        }
    }

    const descargarPlantilla = async () => {
        try {
            manejarCargando(true, undefined, true);

            await modeloTarea.descargarPlantilla();

            mostrarNotificacion(
                EnumTipoNotificacion.Success,
                "Descarga completada",
                "La plantilla de tareas se descargó correctamente.",
                4000
            );
        } catch (error) {
            console.error("Error al descargar la plantilla de tareas:", error);
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                "Error al descargar",
                "No fue posible descargar la plantilla. Verifica tu conexión e intenta nuevamente.",
                5000
            );
        } finally {
            manejarCargando(false);
        }
    };


    const cargaMasivaTareas = async () => {
        try {
            // Crear input de archivo (oculto)
            const input = document.createElement("input");
            input.type = "file";
            input.accept = ".xlsx";
            input.onchange = async (e: any) => {
                const archivo = e.target.files[0];
                if (!archivo) return;

                try {
                    manejarCargando(true, "Procesando archivo...", true);

                    const respuesta = await modeloTarea.cargarMasiva(archivo);

                    if (respuesta.Data) {
                        setDatosRespuestaCarga(respuesta.Data);
                        
                        // Si hay rechazadas, abrimos el modal detallado
                        if (respuesta.Data.Rechazadas && respuesta.Data.Rechazadas.length > 0) {
                            setAbrirModalErrores(true);
                        }
                    }

                    // Mostrar mensajes del backend (si existen)
                    if (respuesta?.Messages?.length > 0) {
                        respuesta.Messages.forEach((mensaje) =>
                            mostrarNotificacion(
                                respuesta.NotificationType ?? EnumTipoNotificacion.Information,
                                "",
                                mensaje,
                                4000
                            )
                        );
                    } else if (respuesta.NotificationType === EnumTipoNotificacion.Success) {
                        mostrarNotificacion(
                            EnumTipoNotificacion.Success,
                            "Carga masiva completada",
                            "El archivo de tareas se cargó correctamente.",
                            4000
                        );
                    }
                    
                    // Refrescar lista de tareas
                    consultarTareas();

                } catch (error) {
                    console.error("Error durante la carga masiva de tareas:", error);
                    mostrarNotificacion(
                        EnumTipoNotificacion.Error,
                        "Error al cargar",
                        "No fue posible procesar el archivo. Verifica el formato o la conexión.",
                        5000
                    );
                } finally {
                    manejarCargando(false);
                }
            };

            // Disparar selector
            input.click();
        } catch (error) {
            console.error("Error inicial en carga masiva de equipos:", error);
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                "Error inesperado",
                "Ocurrió un error al intentar seleccionar el archivo.",
                5000
            );
        }
    };

    const abrirModalExportacionExcel = (estadoModal: boolean) => {
        setEstadoModalExportacionExcel(estadoModal);
        if (!estadoModal) {
            setFiltroTareaExportacion(filtroInicialTarea);
            setListaTareasConsultaExportacion([]);
        }
    }

    const tablaExportacionExcel = useReactTable({
        data: listaTareasConsultaExportacion,
        columns: [],
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        state: { pagination: { pageIndex: pageIndexExportacion, pageSize: PAGE_SIZE_EXPORT }, sorting: sortingExportacion },
        onPaginationChange: updater => {
            if (typeof updater === 'function') {
                const newState = updater({ pageIndex: pageIndexExportacion, pageSize: PAGE_SIZE_EXPORT });
                setPageIndexExportacion(newState.pageIndex);
            } else {
                setPageIndexExportacion(updater.pageIndex ?? 0);
            }
        },
        pageCount: Math.ceil(listaTareasConsultaExportacion.length / PAGE_SIZE_EXPORT),
        onSortingChange: setSortingExportacion,
        getSortedRowModel: getSortedRowModel(),
    });

    const columnsExportar = columnasTablaTarea({
        manejarEdicionTarea: (tarea: ITarea, visualizacion?: boolean) => abrirModalTarea(true, tarea, visualizacion),
        manejarCancelacionTarea: (tarea) => abrirModalCancelarTarea(true, tarea),
        estadoModalExportacionExcel: estadoModalExportacionExcel
    });

    tablaExportacionExcel.setOptions((prev) => ({ ...prev, columns: columnsExportar }));

    const limpiarFiltrosExportacion = () => {
        setFiltroTareaExportacion(filtroInicialTarea);
        setListaTareasConsultaExportacion([]);
    }


    const manejarCambioFiltroTareaExportacion = async (campo: keyof IFiltroTarea, valor: IFiltroTarea[keyof IFiltroTarea]) => {
        if (soloVisitaInstalacion && campo === "TipoServicioId") return;
        setFiltroTareaExportacion((prevFiltroTarea) => ({
            ...prevFiltroTarea,
            [campo]: valor
        }));
    }
    const manejarHabilitarFiltrosExportacion = (habilitar: boolean) => {
        setHabilitarFiltrosExportacion(habilitar)
    }

    const validarCamposConsulta = () => {
        let fechasValidas = true;
        let esValido = true;

        const { FechaInicio, FechaFin, TareaId } = filtroTareaExportacion;

        // 🎯 Si se proporciona un ID de tarea, el rango de fechas deja de ser obligatorio
        if (TareaId) {
            return true;
        }

        // 1️⃣ Validar que existan fechas
        if (!FechaInicio || !FechaInicio.trim()) {
            fechasValidas = false;
        }

        if (!FechaFin || !FechaFin.trim()) {
            fechasValidas = false;
        }

        // 2️⃣ Validar rango máximo de 1 mes
        if (FechaInicio && FechaFin) {
            const fechaInicio = new Date(FechaInicio);
            const fechaFin = new Date(FechaFin);

            const diffTime = fechaFin.getTime() - fechaInicio.getTime();
            const diffDays = diffTime / (1000 * 60 * 60 * 24);

            if (diffDays > 31) {
                esValido = false;

                mostrarNotificacion(
                    EnumTipoNotificacion.Warning,
                    "Rango inválido",
                    "El rango de fechas no puede ser mayor a 31 días.",
                    5000
                );
            }
        }

        if ((FechaFin && FechaInicio) && (FechaFin < FechaInicio)) {
            esValido = false;

            mostrarNotificacion(
                EnumTipoNotificacion.Warning,
                "Rango inválido",
                "La fecha fin no puede ser menor que la fecha inicio.",
                5000
            );
        }

        // 3️⃣ Notificaciones existentes
        if (!fechasValidas) {
            mostrarNotificacion(
                EnumTipoNotificacion.Warning,
                "Campos requeridos (*)",
                "Debes seleccionar un rango de fechas para realizar la consulta.",
                5000
            );
        }

        return fechasValidas && esValido;
    };


    const consultarTareasExportacion = async () => {
        try {
            if (!validarCamposConsulta()) return
            manejarCargando(true, undefined, true);
            const tareas = await modeloTarea.obtenerListaTareas(aplicarRestriccionFiltro(filtroTareaExportacion));
            if (tareas.Data.length === 0) {
                mostrarNotificacion(
                    EnumTipoNotificacion.Information,
                    "Sin resultados",
                    "No se encontraron tareas que coincidan con los filtros seleccionados.",
                    4000
                );
            } else {
                manejarHabilitarFiltrosExportacion(false);
            }
            setListaTareasConsultaExportacion(tareas.Data);
        }
        catch {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                'Error al consultar tareas',
                'No fue posible consultar los tareas. Por favor verifica tu conexión e intenta nuevamente.',
                5000
            );
        }
        finally {
            manejarCargando(false);
        }
    }

    const getCurrentTimestamp = (): string => {
        const now = new Date();

        const pad = (num: number) => num.toString().padStart(2, "0");

        return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
    };

    const exportarTareasExcel = () => {
        if (!listaTareasConsultaExportacion || listaTareasConsultaExportacion.length === 0) {
            mostrarNotificacion(
                EnumTipoNotificacion.Information,
                "Sin datos para exportar",
                "No hay tareas que coincidan con los filtros seleccionados para exportar.",
                4000
            );
            return;
        }
        const timestamp = getCurrentTimestamp();


        exportarExcel(listaTareasConsultaExportacion, columnasExcel, `Reporte_Tareas_${timestamp}`);
    };



    return (
        <>
            <div style={{ height: '100%' }}>
                <TablaTareasVista
                    abrirModalTarea={abrirModalTarea}
                    tabla={tabla}
                    consultarTareas={() => consultarTareas(filtroTarea)}
                    filtro={filtroTarea}
                    habilitarFiltros={habilitarFiltros}
                    manejarCambioFiltroTarea={manejarCambioFiltroTarea}
                    manejarHabilitarFiltros={manejarHabilitarFiltros}
                    entidadesRegistro={entidadesRegistro}
                    limpiarFiltros={limpiarFiltros}
                    cargaMasivaTareas={cargaMasivaTareas}
                    descargarPlantilla={descargarPlantilla}
                    abrirModalExportacionExcel={abrirModalExportacionExcel}
                    estadoModalExportacionExcel={estadoModalExportacionExcel}
                />
            </div>
            <ModalTareaCV
                consultarTareas={consultarTareas}
                abrirModalTarea={abrirModalTarea}
                estadoModal={abrirModal}
                entidadTarea={entidadTarea}
                setEntidadTarea={setEntidadTarea}
                visualizacion={visualizacion}
                esEmergencia={false}
                entidadesRegistro={entidadesRegistro}
            />
            <ModalCancelarTareaCV
                abrirModalCancelarTarea={abrirModalCancelarTarea}
                estadoModal={abrirModalCancelar}
                tareaSeleccionado={tareaParaCancelar}
            />

            <ModalExportacionExcelVista
                estadoModal={estadoModalExportacionExcel}
                abrirModalExportacionExcel={abrirModalExportacionExcel}
                tabla={tablaExportacionExcel}
                filtro={filtroTareaExportacion}
                entidadesRegistro={entidadesRegistro}
                consultarTareas={consultarTareasExportacion}
                limpiarFiltros={limpiarFiltrosExportacion}
                manejarCambioFiltroTarea={manejarCambioFiltroTareaExportacion}
                manejarHabilitarFiltros={manejarHabilitarFiltrosExportacion}
                habilitarFiltros={habilitarFiltrosExportacion}
                hayRegistros={listaTareasConsultaExportacion.length > 0}
                exportarTareasExcel={exportarTareasExcel}
            />

            <ModalErroresCargaMasiva
                abrir={abrirModalErrores}
                onClose={() => setAbrirModalErrores(false)}
                rechazadas={datosRespuestaCarga.Rechazadas}
                totales={{
                    guardadas: datosRespuestaCarga.TareasGuardadas,
                    rechazadas: datosRespuestaCarga.TareasRechazadas
                }}
            />
        </>
    )
}

export default TareasCV;